import { IconPencil, IconTrash } from "@tabler/icons-react";
import { useTranslations } from "next-intl";
import ClassicLoader from "@/components/common/classic-loader";
import { UnderlineAnimation } from "@/components/common/underline-animation";
import { ListingItemHeader } from "@/components/listing/listing-item-header";
import { Button, buttonVariants } from "@/components/ui/button";
import { RemoveFromCartForm } from "@/features/cart/components/remove-from-cart";
import type { CartItem } from "@/features/cart/types";
import { DietDetailsDrawer } from "@/features/diets/components/diet-details-drawer";
import type { DietCatalog } from "@/features/diets/types";
import { Link } from "@/i18n/navigation";
import { formatDietName } from "@/lib/helpers/format-brand-name";
import { type FilterableParams, getConfiguratorParams } from "@/lib/utilities";
import { cn } from "@/lib/utils";

export const CartDietHeader = ({
  diet,
  item,
}: {
  diet: DietCatalog;
  item: CartItem;
}) => {
  const t = useTranslations();
  const configParams = getConfiguratorParams<FilterableParams>({
    configuration: diet.searchResultId || "",
  });

  return (
    <div className="flex w-full flex-col md:flex-row">
      <div className="mb-1">
        <ListingItemHeader
          componentPrimary={
            <DietDetailsDrawer
              dietId={diet.id}
              label={
                <>
                  {formatDietName(diet.name)}
                  <UnderlineAnimation />
                </>
              }
              showCta={false}
              configuration={configParams}
            />
          }
        />
      </div>
      <div className="flex items-start gap-4 md:ml-auto">
        <RemoveFromCartForm
          id={item.configurationItem.id}
          variant="ghost"
          customButton={(pending) => (
            <Button
              disabled={pending}
              variant="ghost"
              size="sm"
              className="h-auto min-h-auto p-0 text-base underline underline-offset-4 hover:bg-transparent hover:no-underline"
              data-pw="remove-diet-button"
            >
              {pending ? (
                <ClassicLoader className="mr-2 size-6 animate-spin" />
              ) : (
                <IconTrash className="mr-2 size-6" />
              )}
              {t("cart.remove")}
            </Button>
          )}
        />
        <Link
          href={{
            pathname: "/cart/[id]",
            params: { id: item.configurationItem.id || "" },
          }}
          className={cn(
            buttonVariants({ variant: "ghost" }),
            "h-auto min-h-auto p-0 text-base underline underline-offset-4 hover:bg-transparent hover:no-underline",
          )}
          data-pw="edit-diet-button"
        >
          <IconPencil className="mr-2 size-6" />
          <span className="block">{t("cart.edit")}</span>
        </Link>
      </div>
    </div>
  );
};
